import { useEffect, useRef, useState } from 'react'

/**
 * Peak level (dBFS) of a MediaStream, sampled via an AnalyserNode.
 * Returns -100 when silent / no stream.
 *
 * Default update throttle is ~12 fps (80 ms) — fine for the simple bar
 * meter. Pro meters (LevelMeterPro) want every analyser frame so 1 ms attack
 * ballistics catch short peaks: pass `{ intervalMs: 0 }` (or ~16) for that.
 */
export function useAudioLevel(
  stream: MediaStream | null,
  opts?: { intervalMs?: number },
): number {
  const intervalMs = opts?.intervalMs ?? 80
  const [db, setDb] = useState(-100)
  const lastUpdateRef = useRef(0)

  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0) {
      setDb(-100)
      return
    }
    const ctx = new AudioContext()
    const source = ctx.createMediaStreamSource(stream)
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 2048
    source.connect(analyser)
    const buf = new Float32Array(analyser.fftSize)
    let raf = 0
    let cancelled = false

    const tick = () => {
      if (cancelled) return
      analyser.getFloatTimeDomainData(buf)
      let peak = 0
      for (let i = 0; i < buf.length; i++) {
        const v = Math.abs(buf[i] ?? 0)
        if (v > peak) peak = v
      }
      const now = performance.now()
      if (now - lastUpdateRef.current >= intervalMs) {
        lastUpdateRef.current = now
        setDb(peak > 0 ? Math.max(-100, 20 * Math.log10(peak)) : -100)
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      source.disconnect()
      void ctx.close().catch(() => undefined)
    }
  }, [stream, intervalMs])

  return db
}
